import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

let app: any;
let auth: any;
let googleProvider: any;
let isInitialized = false;

/**
 * Initialize Firebase with config from server
 */
export async function initializeFirebase(): Promise<void> {
  if (isInitialized) return;

  try {
    // Fetch Firebase config from API
    const response = await fetch(`${API_URL}/config/firebase`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch Firebase config: ${response.status}`);
    }

    const firebaseConfig = await response.json();
    
    if (!firebaseConfig.apiKey) {
      throw new Error('Firebase API key not configured on server');
    }

    // Initialize Firebase
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    throw error;
  }
}

/**
 * Ensure Firebase is initialized
 */
async function ensureInitialized(): Promise<void> {
  if (!isInitialized) {
    await initializeFirebase();
  }
}

/**
 * Sign in with Google
 * Uses popup first, falls back to redirect if popup is blocked
 */
export async function signInWithGoogle(): Promise<User> {
  await ensureInitialized();
  
  try {
    // Try popup first
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error: any) {
    // If popup fails (blocked or closed), use redirect method
    if (error.code === 'auth/popup-closed-by-user' || 
        error.code === 'auth/popup-blocked' ||
        error.code === 'auth/cancelled-popup-request') {
      console.log('Popup failed, using redirect method...');
      await signInWithRedirect(auth, googleProvider);
      // This won't return immediately - page will redirect
      throw new Error('Redirecting to Google sign-in...');
    }
    throw error;
  }
}

/**
 * Check for redirect result on page load
 */
export async function checkRedirectResult(): Promise<User | null> {
  await ensureInitialized();
  try {
    const result = await getRedirectResult(auth);
    return result?.user || null;
  } catch (error) {
    console.error('Error getting redirect result:', error);
    return null;
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  await ensureInitialized();
  await firebaseSignOut(auth);
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User | null> {
  await ensureInitialized();
  return auth.currentUser;
}

/**
 * Subscribe to auth state changes
 */
export async function onAuthStateChange(callback: (user: User | null) => void): Promise<() => void> {
  await ensureInitialized();
  return onAuthStateChanged(auth, callback);
}

/**
 * Get ID token for API calls
 */
export async function getIdToken(): Promise<string | null> {
  await ensureInitialized();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Check if Firebase is initialized
 */
export function isFirebaseInitialized(): boolean {
  return isInitialized;
}

/**
 * Get auth instance (only after initialization)
 */
export function getAuth_(): any {
  return auth;
}
